import { useParams } from 'react-router-dom';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { getClienteInfo, getChatHistorial, insertarComentario, insertarVisita, getConcentracionDeudor, getGraficoVolumen, getAntecedentesSocietarios, getMaximosDeudores, getInfoEmpresa, getGraficoDicom, getAnotacionesComerciales, getUltimoDicom, getGraficoFactoring, getDetalleCesionesCliente, getDetalleFacturas, getUsuarios } from '../api/apiConfig';
import { useUser } from '../context/UserContext';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';
import { MdSend, MdPerson, MdAccountCircle, MdShowChart, MdReceipt, MdEventNote } from 'react-icons/md';
import { FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';
import * as XLSX from 'xlsx';
import { sortData } from '../utils/tableSortSearch';

/** Clave tal como viene del API en detalle cesiones */
const CESION_DEUDOR_KEY = 'IFNULL(DEU.RAZON_SOC,"")';
const CESION_MONTO_KEY = 'MNT_CESION/1000000';
const TIPO_INSTALACION_OPTIONS = [
  { value: 'OFICINA_VIRTUAL', label: 'Oficina virtual' },
  { value: 'FABRICA_PLANTA', label: 'Fábrica/Planta' },
  { value: 'OFICINAS', label: 'Oficinas' },
  { value: 'OTRO', label: 'Otro' },
];

const DetalleCliente = () => {
  const { rut } = useParams();
  const { user } = useUser();
  const [clienteInfo, setClienteInfo] = useState(null);
  const [chatHistorial, setChatHistorial] = useState([]);
  /** 'desc' = más nuevos primero (default); 'asc' = más antiguos primero */
  const [chatOrden, setChatOrden] = useState('desc');
  const [concentracionDeudor, setConcentracionDeudor] = useState([]);
  const [graficoVolumen, setGraficoVolumen] = useState([]);
  const [antecedentesSocietarios, setAntecedentesSocietarios] = useState([]);
  const [maximosDeudores, setMaximosDeudores] = useState([]);
  const [infoEmpresa, setInfoEmpresa] = useState(null);
  const [graficoDicom, setGraficoDicom] = useState([]);
  const [anotacionesComerciales, setAnotacionesComerciales] = useState([]);
  const [nuevoComentario, setNuevoComentario] = useState('');
  const [mostrarFormularioVisita, setMostrarFormularioVisita] = useState(false);
  const [usuariosSistema, setUsuariosSistema] = useState([]);
  const [visitaForm, setVisitaForm] = useState({
    fecha_visita: '',
    realizada_por: '',
    direccion: '',
    tipo_instalacion: '',
    observaciones: '',
  });
  const [loading, setLoading] = useState(true);
  const [loadingChat, setLoadingChat] = useState(true);
  const [loadingConcentracion, setLoadingConcentracion] = useState(true);
  const [loadingGrafico, setLoadingGrafico] = useState(true);
  const [loadingAntecedentes, setLoadingAntecedentes] = useState(true);
  const [loadingMaximos, setLoadingMaximos] = useState(true);
  const [loadingInfoEmpresa, setLoadingInfoEmpresa] = useState(true);
  const [loadingDicom, setLoadingDicom] = useState(true);
  const [loadingAnotaciones, setLoadingAnotaciones] = useState(true);
  const [ultimoDicom, setUltimoDicom] = useState(null);
  const [loadingUltimoDicom, setLoadingUltimoDicom] = useState(true);
  const [graficoFactoring, setGraficoFactoring] = useState([]);
  const [loadingFactoring, setLoadingFactoring] = useState(true);
  const [detalleCesiones, setDetalleCesiones] = useState([]);
  const [loadingCesiones, setLoadingCesiones] = useState(true);
  const [detalleFacturas, setDetalleFacturas] = useState([]);
  const [loadingFacturas, setLoadingFacturas] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentPageCesiones, setCurrentPageCesiones] = useState(1);
  const [currentPageFacturas, setCurrentPageFacturas] = useState(1);
  const [error, setError] = useState(null);

  const [sortMaximos, setSortMaximos] = useState({ column: null, direction: 'asc' });
  const [sortAnotaciones, setSortAnotaciones] = useState({ column: null, direction: 'asc' });
  const [sortCesiones, setSortCesiones] = useState({ column: null, direction: 'asc' });
  const [sortFacturas, setSortFacturas] = useState({ column: null, direction: 'asc' });

  const formatDateKey = (dateStr) => {
    if (!dateStr) return 0;
    const parsed = new Date(dateStr);
    if (!Number.isNaN(parsed.getTime())) return parsed.getTime();
    const normalizedDate = new Date(`${dateStr}T00:00:00`);
    return Number.isNaN(normalizedDate.getTime()) ? 0 : normalizedDate.getTime();
  };

  const ordenarHistorial = useCallback((items, orden = chatOrden) => {
    const factor = orden === 'desc' ? -1 : 1;
    return [...items].sort(
      (a, b) =>
        factor
        * (formatDateKey(a.FECHA_EVENTO || a.FECHA_VISITA || a.FECHA)
          - formatDateKey(b.FECHA_EVENTO || b.FECHA_VISITA || b.FECHA)),
    );
  }, [chatOrden]);

  const chatHistorialOrdenado = useMemo(
    () => ordenarHistorial(chatHistorial),
    [chatHistorial, ordenarHistorial],
  );

  const todayIso = () => new Date().toISOString().split('T')[0];

  const isSuccessfulMutationResponse = (response) => {
    if (!response || typeof response !== 'object') return false;
    if (response.success === false) return false;
    if (response.error) return false;
    return true;
  };

  const getUsuarioLabel = (registro) => {
    if (!registro) return '';
    if (typeof registro === 'string') return registro.trim();
    const keys = ['nombre', 'NOM_EJEC', 'nom_ejec', 'ejec_sist_factoring', 'ejec_sist_fact', 'name', 'NOMBRE', 'mail', 'email'];
    for (const key of keys) {
      const value = registro[key];
      if (typeof value === 'string' && value.trim()) return value.trim();
    }
    return '';
  };

  const normalizarActividad = (actividad) => {
    const tipoRegistro = (actividad?.TIPO_REGISTRO || (actividad?.CHAT ? 'COMENTARIO' : 'VISITA')).toUpperCase();
    return {
      ...actividad,
      TIPO_REGISTRO: tipoRegistro,
      FECHA_EVENTO: actividad?.FECHA_EVENTO || actividad?.FECHA_VISITA || actividad?.FECHA,
    };
  };

  const formatTipoInstalacion = (tipo) =>
    TIPO_INSTALACION_OPTIONS.find((item) => item.value === tipo)?.label || tipo || 'No disponible';

  const getSortIcon = (column, state) => {
    if (state.column !== column) return <FaSort style={{ marginLeft: '4px', opacity: 0.3 }} />;
    return state.direction === 'asc' ? <FaSortUp style={{ marginLeft: '4px' }} /> : <FaSortDown style={{ marginLeft: '4px' }} />;
  };

  const thSortable = { cursor: 'pointer', userSelect: 'none' };

  const handleSortMaximos = (column) => {
    setSortMaximos((prev) =>
      prev.column === column
        ? { column, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { column, direction: 'asc' },
    );
  };

  const handleSortAnotaciones = (column) => {
    setSortAnotaciones((prev) =>
      prev.column === column
        ? { column, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { column, direction: 'asc' },
    );
    setCurrentPage(1);
  };

  const handleSortCesiones = (column) => {
    setSortCesiones((prev) =>
      prev.column === column
        ? { column, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { column, direction: 'asc' },
    );
    setCurrentPageCesiones(1);
  };

  const handleSortFacturas = (column) => {
    setSortFacturas((prev) =>
      prev.column === column
        ? { column, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { column, direction: 'asc' },
    );
    setCurrentPageFacturas(1);
  };

  const sortedMaximosDeudores = useMemo(
    () => sortData(maximosDeudores, sortMaximos.column, sortMaximos.direction),
    [maximosDeudores, sortMaximos.column, sortMaximos.direction],
  );

  const sortedAnotaciones = useMemo(
    () =>
      sortData(anotacionesComerciales, sortAnotaciones.column, sortAnotaciones.direction, {
        dateColumns: ['fechaVencimiento', 'fechaPublicacion'],
      }),
    [anotacionesComerciales, sortAnotaciones.column, sortAnotaciones.direction],
  );

  const sortedDetalleCesiones = useMemo(
    () =>
      sortData(detalleCesiones, sortCesiones.column, sortCesiones.direction, {
        dateColumns: ['FCH_CESION'],
      }),
    [detalleCesiones, sortCesiones.column, sortCesiones.direction],
  );

  const sortedDetalleFacturas = useMemo(
    () =>
      sortData(detalleFacturas, sortFacturas.column, sortFacturas.direction, {
        dateColumns: ['FechaDocto'],
      }),
    [detalleFacturas, sortFacturas.column, sortFacturas.direction],
  );

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const [year, month, day] = dateStr.split('-');
    return `${day}-${month}-${year}`;
  };

  const formatDateDDMMYYYY = (dateStr) => {
    if (!dateStr || dateStr === '0000-00-00') return 'No disponible';
    
    // Handle format with time: "2026-02-10 00:00:00"
    if (dateStr.includes(' ')) {
      const datePart = dateStr.split(' ')[0]; // Get only the date part
      const [year, month, day] = datePart.split('-');
      if (!year || !month || !day) return 'No disponible';
      return `${day}-${month}-${year}`;
    }
    
    // Handle standard format: "2026-02-10"
    const [year, month, day] = dateStr.split('-');
    if (!year || !month || !day) return 'No disponible';
    return `${day}-${month}-${year}`;
  };

  const enviarComentario = async () => {
    if (!nuevoComentario.trim() || !user) return;
    
    const fechaActual = todayIso();
    const comentarioTexto = nuevoComentario;
    const temporalId = `temp-comentario-${Date.now()}`;
    
    // Crear el nuevo mensaje para mostrar inmediatamente
    const nuevoMensaje = {
      ID_EVENTO: temporalId,
      TIPO_REGISTRO: 'COMENTARIO',
      CHAT: comentarioTexto,
      NOM_EJEC: user.nombre || 'Usuario',
      TIPO_USUARIO: user.tipo_usuario || 'Usuario',
      FECHA: fechaActual,
      FECHA_EVENTO: fechaActual,
    };
    
    // Agregar inmediatamente al historial
    setChatHistorial((prev) => [...prev, nuevoMensaje]);
    setNuevoComentario('');
    
    try {
      await insertarComentario(
        rut,
        comentarioTexto,
        user.ejec_sist_factoring || 'N/A',
        user.nombre || 'Usuario',
        user.tipo_usuario || 'Usuario',
        fechaActual
      );
    } catch (error) {
      console.error('Error enviando comentario:', error);
      // Si falla, remover el mensaje del historial
      setChatHistorial(prev => prev.filter((item) => item.ID_EVENTO !== temporalId));
    }
  };

  const enviarVisita = async () => {
    if (!rut || !user) return;

    const payload = {
      rut,
      fecha_visita: visitaForm.fecha_visita,
      realizada_por: visitaForm.realizada_por,
      direccion: visitaForm.direccion.trim(),
      tipo_instalacion: visitaForm.tipo_instalacion,
      observaciones: visitaForm.observaciones.trim(),
      nom_ejec: user.nombre || visitaForm.realizada_por || 'Usuario',
      tipo_usuario: user.tipo_usuario || 'Usuario',
    };

    if (!payload.fecha_visita || !payload.realizada_por || !payload.direccion || !payload.tipo_instalacion) {
      return;
    }

    const temporalId = `temp-visita-${Date.now()}`;
    const visitaOptimista = {
      ID_EVENTO: temporalId,
      TIPO_REGISTRO: 'VISITA',
      FECHA_EVENTO: `${payload.fecha_visita}T00:00:00`,
      FECHA: todayIso(),
      FECHA_VISITA: payload.fecha_visita,
      NOM_EJEC: payload.realizada_por,
      TIPO_USUARIO: 'Visita',
      REALIZADA_POR: payload.realizada_por,
      DIRECCION: payload.direccion,
      TIPO_INSTALACION: payload.tipo_instalacion,
      OBSERVACIONES: payload.observaciones,
    };

    setChatHistorial((prev) => [...prev, visitaOptimista]);
    setMostrarFormularioVisita(false);
    setVisitaForm({
      fecha_visita: todayIso(),
      realizada_por: user.nombre || '',
      direccion: '',
      tipo_instalacion: '',
      observaciones: '',
    });

    try {
      const visitaResponse = await insertarVisita(payload);
      if (!isSuccessfulMutationResponse(visitaResponse)) {
        throw new Error(visitaResponse?.message || visitaResponse?.error || 'No se pudo registrar la visita');
      }

      const chatData = await getChatHistorial(rut);
      const historial = Array.isArray(chatData) ? chatData.map(normalizarActividad) : [];
      setChatHistorial(historial);
    } catch (error) {
      console.error('Error enviando visita:', error);
      setChatHistorial((prev) => prev.filter((item) => item.ID_EVENTO !== temporalId));
    }
  };

  useEffect(() => {
    const fetchUsuariosSistema = async () => {
      try {
        const usuariosResponse = await getUsuarios();
        const usuariosRaw = Array.isArray(usuariosResponse)
          ? usuariosResponse
          : Array.isArray(usuariosResponse?.data)
            ? usuariosResponse.data
            : [];
        const nombres = usuariosRaw
          .map(getUsuarioLabel)
          .filter(Boolean);
        if (user?.nombre) {
          nombres.unshift(user.nombre);
        }
        const usuariosUnicos = [...new Set(nombres)];
        setUsuariosSistema(usuariosUnicos);
      } catch (usuariosError) {
        console.error('Error cargando listado de usuarios:', usuariosError);
        setUsuariosSistema(user?.nombre ? [user.nombre] : []);
      }
    };

    fetchUsuariosSistema();
  }, [user?.nombre]);

  useEffect(() => {
    const fetchData = async () => {
      if (!rut) return;
      
      try {
        const clienteData = await getClienteInfo(rut);
        setClienteInfo(clienteData);
        setLoading(false);
        
        try {
          const chatData = await getChatHistorial(rut);
          console.log('Chat data received:', chatData);
          const historial = Array.isArray(chatData) ? chatData.map(normalizarActividad) : [];
          setChatHistorial(historial);
        } catch (chatError) {
          console.error('Error fetching chat:', chatError);
          setChatHistorial([]);
        }
        setLoadingChat(false);
        
        try {
          const concentracionData = await getConcentracionDeudor(rut);
          console.log('Concentracion data received:', concentracionData);
          setConcentracionDeudor(concentracionData || []);
        } catch (concentracionError) {
          console.error('Error fetching concentracion:', concentracionError);
          setConcentracionDeudor([]);
        }
        setLoadingConcentracion(false);
        
        try {
          const graficoData = await getGraficoVolumen(rut);
          console.log('Grafico data received:', graficoData);
          setGraficoVolumen(graficoData || []);
        } catch (graficoError) {
          console.error('Error fetching grafico:', graficoError);
          setGraficoVolumen([]);
        }
        setLoadingGrafico(false);
        
        try {
          const antecedentesData = await getAntecedentesSocietarios(rut);
          console.log('Antecedentes data received:', antecedentesData);
          setAntecedentesSocietarios(antecedentesData || []);
        } catch (antecedentesError) {
          console.error('Error fetching antecedentes:', antecedentesError);
          setAntecedentesSocietarios([]);
        }
        setLoadingAntecedentes(false);
        
        try {
          const maximosData = await getMaximosDeudores(rut);
          console.log('Maximos deudores data received:', maximosData);
          setMaximosDeudores(maximosData || []);
        } catch (maximosError) {
          console.error('Error fetching maximos deudores:', maximosError);
          setMaximosDeudores([]);
        }
        setLoadingMaximos(false);
        
        try {
          const infoEmpresaData = await getInfoEmpresa(rut);
          console.log('Info empresa data received:', infoEmpresaData);
          setInfoEmpresa(infoEmpresaData?.[0] || null);
        } catch (infoEmpresaError) {
          console.error('Error fetching info empresa:', infoEmpresaError);
          setInfoEmpresa(null);
        }
        setLoadingInfoEmpresa(false);
        
        try {
          const dicomData = await getGraficoDicom(rut);
          console.log('DICOM data received:', dicomData);
          setGraficoDicom(dicomData || []);
        } catch (dicomError) {
          console.error('Error fetching DICOM:', dicomError);
          setGraficoDicom([]);
        }
        setLoadingDicom(false);
        
        try {
          const anotacionesData = await getAnotacionesComerciales(rut);
          console.log('Anotaciones data received:', anotacionesData);
          console.log('Anotaciones array length:', anotacionesData?.length);
          setAnotacionesComerciales(Array.isArray(anotacionesData) ? anotacionesData : []);
        } catch (anotacionesError) {
          console.error('Error fetching anotaciones:', anotacionesError);
          setAnotacionesComerciales([]);
        }
        setLoadingAnotaciones(false);
        
        try {
          const ultimoDicomData = await getUltimoDicom(rut);
          console.log('Ultimo DICOM data received:', ultimoDicomData);
          setUltimoDicom(ultimoDicomData?.[0] || null);
        } catch (ultimoDicomError) {
          console.error('Error fetching ultimo DICOM:', ultimoDicomError);
          setUltimoDicom(null);
        }
        setLoadingUltimoDicom(false);
        
        try {
          const factoringData = await getGraficoFactoring(rut);
          console.log('Factoring data received:', factoringData);
          console.log('Factoring data length:', factoringData?.length);
          console.log('Factoring data items:', factoringData);
          setGraficoFactoring(factoringData || []);
        } catch (factoringError) {
          console.error('Error fetching factoring:', factoringError);
          setGraficoFactoring([]);
        }
        setLoadingFactoring(false);
        
        try {
          const cesionesData = await getDetalleCesionesCliente(rut);
          console.log('Cesiones data received:', cesionesData);
          setDetalleCesiones(cesionesData || []);
        } catch (cesionesError) {
          console.error('Error fetching cesiones:', cesionesError);
          setDetalleCesiones([]);
        }
        setLoadingCesiones(false);
        
        try {
          const facturasData = await getDetalleFacturas(rut);
          console.log('Facturas data received:', facturasData);
          setDetalleFacturas(facturasData || []);
        } catch (facturasError) {
          console.error('Error fetching facturas:', facturasError);
          setDetalleFacturas([]);
        }
        setLoadingFacturas(false);
      } catch (error) {
        console.error('Error fetching cliente info:', error);
        setError('Error al cargar la información del cliente');
        setLoading(false);
        setLoadingChat(false);
        setLoadingConcentracion(false);
        setLoadingGrafico(false);
        setLoadingAntecedentes(false);
        setLoadingMaximos(false);
        setLoadingInfoEmpresa(false);
        setLoadingDicom(false);
        setLoadingAnotaciones(false);
        setLoadingUltimoDicom(false);
        setLoadingFactoring(false);
        setLoadingCesiones(false);
        setLoadingFacturas(false);
      }
    };

    fetchData();
  }, [rut]);

  return (
    <div className="p-6">
      <div className="sticky-header">
        <h1 className="text-2xl font-bold text-gray-800">{clienteInfo?.CLIENTE || infoEmpresa?.NombreORazonSocial || 'Detalle Cliente'}</h1>
        <p className="text-gray-600">RUT: {rut}</p>
      </div>

      <div className="cliente-container">
        <div className="cliente-info">
          <div className="seccion-datos">
            <h3 style={{color: 'var(--if-primary)', margin: '0 0 1rem 0', fontSize: '1.2rem', fontWeight: '600', borderLeft: '4px solid var(--if-accent)', paddingLeft: '1rem'}}>
              Información del Cliente (Sistema Factoring)
            </h3>
            {loading ? (
              <div className="flex justify-center items-center h-32">
                <AiOutlineLoading3Quarters className="animate-spin h-8 w-8" style={{color: 'var(--if-primary)'}} />
              </div>
            ) : error ? (
              <div className="text-center py-8 text-red-600">
                {error}
              </div>
            ) : clienteInfo ? (
              <div className="info-empresa">
                <p><strong>RUT:</strong> {clienteInfo.RUT_CLI}</p>
                <p><strong>Cliente:</strong> {clienteInfo.CLIENTE}</p>
                <p><strong>Ejecutivo:</strong> {clienteInfo.EJECUTIVO}</p>
                <p><strong>Estado de Línea:</strong> {clienteInfo.ESTADO}</p>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-600">
                No se encontró información para este cliente
              </div>
            )}
          </div>
        </div>
        
        <div className="chat-container">
          <div className="seccion-datos">
            <div className="chat-historial-header">
              <h3 style={{color: 'var(--if-primary)', margin: 0, fontSize: '1.2rem', fontWeight: '600', borderLeft: '4px solid var(--if-accent)', paddingLeft: '1rem'}}>
                Historial de actividad
              </h3>
              <div className="chat-orden-toggle" role="group" aria-label="Orden del historial">
                <button
                  type="button"
                  className={`chat-orden-btn ${chatOrden === 'desc' ? 'active' : ''}`}
                  onClick={() => setChatOrden('desc')}
                  aria-pressed={chatOrden === 'desc'}
                >
                  Más recientes primero
                </button>
                <button
                  type="button"
                  className={`chat-orden-btn ${chatOrden === 'asc' ? 'active' : ''}`}
                  onClick={() => setChatOrden('asc')}
                  aria-pressed={chatOrden === 'asc'}
                >
                  Más antiguos primero
                </button>
              </div>
            </div>
            
            <div className="chat-messages">
              {loadingChat ? (
                <div className="flex justify-center items-center h-32">
                  <AiOutlineLoading3Quarters className="animate-spin h-8 w-8" style={{color: 'var(--if-primary)'}} />
                </div>
              ) : chatHistorialOrdenado.length > 0 ? (
                chatHistorialOrdenado.map((mensaje, index) => {
                  const tipoRegistro = (mensaje.TIPO_REGISTRO || 'COMENTARIO').toUpperCase();
                  if (tipoRegistro === 'VISITA') {
                    return (
                      <div key={mensaje.ID_EVENTO || index} className="chat-bubble-container visita">
                        <MdEventNote className="user-avatar visita-avatar" />
                        <div className="chat-bubble visita-bubble">
                          <div className="bubble-header">
                            <span className="user-name">Registro de visita</span>
                            <span className="message-date">{formatDateDDMMYYYY(mensaje.FECHA_VISITA || mensaje.FECHA_EVENTO || mensaje.FECHA)}</span>
                          </div>
                          <div className="visita-content">
                            <p><strong>Realizada por:</strong> {mensaje.REALIZADA_POR || mensaje.NOM_EJEC || 'No disponible'}</p>
                            <p><strong>Dirección:</strong> {mensaje.DIRECCION || 'No disponible'}</p>
                            <p><strong>Tipo de instalación:</strong> {formatTipoInstalacion(mensaje.TIPO_INSTALACION)}</p>
                            <p><strong>Observaciones:</strong> {mensaje.OBSERVACIONES || 'Sin observaciones'}</p>
                          </div>
                        </div>
                      </div>
                    );
                  }

                  const isEjecutivo = mensaje.TIPO_USUARIO === 'Ejecutivo';
                  return (
                    <div 
                      key={mensaje.ID_EVENTO || index}
                      className={`chat-bubble-container ${isEjecutivo ? 'ejecutivo' : 'otros'}`}
                      style={{
                        display: 'flex',
                        flexDirection: isEjecutivo ? 'row-reverse' : 'row',
                        marginLeft: isEjecutivo ? '0' : '0',
                        marginRight: isEjecutivo ? '0' : '0'
                      }}
                    >
                      {isEjecutivo ? 
                        <MdAccountCircle className="user-avatar ejecutivo-avatar" style={{ marginLeft: '8px', marginRight: '0' }} /> : 
                        <MdPerson className="user-avatar otros-avatar" style={{ marginRight: '8px', marginLeft: '0' }} />
                      }
                      <div className="chat-bubble" style={{ textAlign: isEjecutivo ? 'right' : 'left' }}>
                        <div className="bubble-header" style={{ justifyContent: isEjecutivo ? 'flex-end' : 'flex-start' }}>
                          <span className="user-name">{mensaje.NOM_EJEC} ({mensaje.TIPO_USUARIO})</span>
                          <span className="message-date">{formatDateDDMMYYYY(mensaje.FECHA || mensaje.FECHA_EVENTO)}</span>
                        </div>
                        <div className="bubble-content">
                          {mensaje.CHAT}
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="text-center py-8 text-gray-600">
                  No hay actividad aún
                </div>
              )}
            </div>
            
            <div className="chat-input">
              <input
                type="text"
                value={nuevoComentario}
                onChange={(e) => setNuevoComentario(e.target.value)}
                placeholder="Deja tu comentario acá..."
                className="comment-input"
                onKeyDown={(e) => e.key === 'Enter' && enviarComentario()}
              />
              <button
                onClick={enviarComentario}
                className="send-button"
                disabled={!nuevoComentario.trim()}
              >
                <MdSend className="w-4 h-4" />
              </button>
            </div>
            <div className="chat-toolbar">
              <button
                onClick={() => {
                  const nuevoEstado = !mostrarFormularioVisita;
                  setMostrarFormularioVisita(nuevoEstado);
                  if (nuevoEstado) {
                    setVisitaForm((prev) => ({
                      ...prev,
                      fecha_visita: prev.fecha_visita || todayIso(),
                      realizada_por: prev.realizada_por || user?.nombre || '',
                    }));
                  }
                }}
                className="visit-toggle-button"
              >
                {mostrarFormularioVisita ? 'Cancelar registro de visita' : 'Registrar visita'}
              </button>
            </div>
            {mostrarFormularioVisita && (
              <div className="visita-form">
                <div className="visita-form-grid">
                  <label>
                    Fecha de visita
                    <input
                      type="date"
                      value={visitaForm.fecha_visita}
                      onChange={(e) => setVisitaForm((prev) => ({ ...prev, fecha_visita: e.target.value }))}
                    />
                  </label>
                  <label>
                    Realizada por
                    <select
                      value={visitaForm.realizada_por}
                      onChange={(e) => setVisitaForm((prev) => ({ ...prev, realizada_por: e.target.value }))}
                    >
                      <option value="">Seleccionar usuario</option>
                      {usuariosSistema.map((nombreUsuario) => (
                        <option key={nombreUsuario} value={nombreUsuario}>
                          {nombreUsuario}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Dirección
                    <input
                      type="text"
                      value={visitaForm.direccion}
                      onChange={(e) => setVisitaForm((prev) => ({ ...prev, direccion: e.target.value }))}
                      placeholder="Dirección de la visita"
                    />
                  </label>
                  <label>
                    Tipo de instalación
                    <select
                      value={visitaForm.tipo_instalacion}
                      onChange={(e) => setVisitaForm((prev) => ({ ...prev, tipo_instalacion: e.target.value }))}
                    >
                      <option value="">Seleccionar tipo</option>
                      {TIPO_INSTALACION_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
                <label className="visita-observaciones">
                  Observaciones
                  <textarea
                    value={visitaForm.observaciones}
                    onChange={(e) => setVisitaForm((prev) => ({ ...prev, observaciones: e.target.value }))}
                    placeholder="Observaciones de la visita"
                    rows={3}
                  />
                </label>
                <div className="visita-actions">
                  <button
                    onClick={enviarVisita}
                    className="send-button"
                    disabled={
                      !visitaForm.fecha_visita ||
                      !visitaForm.realizada_por ||
                      !visitaForm.direccion.trim() ||
                      !visitaForm.tipo_instalacion
                    }
                  >
                    Guardar visita
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      
      <div className="mt-6">
        <div className="seccion-datos">
          <h3 style={{color: 'var(--if-primary)', margin: '0 0 1rem 0', fontSize: '1.2rem', fontWeight: '600', borderLeft: '4px solid var(--if-accent)', paddingLeft: '1rem'}}>
            Información de la Empresa (Informax)
          </h3>
          {loadingInfoEmpresa ? (
            <div className="flex justify-center items-center h-32">
              <AiOutlineLoading3Quarters className="animate-spin h-8 w-8" style={{color: 'var(--if-primary)'}} />
            </div>
          ) : infoEmpresa ? (
            <div className="grid grid-cols-3 gap-6">
              <div className="info-empresa">
                <p><strong>Razón Social:</strong> {infoEmpresa.NombreORazonSocial}</p>
                <p><strong>Ubicación:</strong> {infoEmpresa.Direccion}</p>
                <p><strong>Teléfonos:</strong> {infoEmpresa.Telefonos || 'No disponible'}</p>
                <p><strong>Email:</strong> {infoEmpresa.Email || 'No disponible'}</p>
              </div>
              <div className="info-empresa">
                <p><strong>Inicio Actividades:</strong> {formatDateDDMMYYYY(infoEmpresa.AnoInicioActividades)}</p>
                <p><strong>Tamaño Subsegmento:</strong> {infoEmpresa.TamanoSubsegmento}</p>
                <p><strong>Rango Empresa:</strong> {infoEmpresa.TamanoEmpresaRango}</p>
                <p><strong>Número Empleados:</strong> {infoEmpresa.NumeroEmpleados}</p>
                <p><strong>Número Vehículos:</strong> {infoEmpresa.NumeroVehiculos}</p>
              </div>
              <div className="info-empresa">
                <p><strong>Subsector:</strong> {infoEmpresa.Subsector}</p>
                <p><strong>Actividad Económica:</strong> {infoEmpresa.ActividadEconomica}</p>
                
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-600">
              No se encontró información de la empresa
            </div>
          )}
        </div>
      </div>
      
      <div className="grid grid-cols-3 gap-6 mt-6">
        <div className="seccion-datos">
          <h3 style={{color: 'var(--if-primary)', margin: '0 0 1rem 0', fontSize: '1.2rem', fontWeight: '600', borderLeft: '4px solid var(--if-accent)', paddingLeft: '1rem'}}>
            Top Stock Deudor (Sistema Factoring)
          </h3>
          {loadingConcentracion ? (
            <div className="flex justify-center items-center h-32">
              <AiOutlineLoading3Quarters className="animate-spin h-8 w-8" style={{color: 'var(--if-primary)'}} />
            </div>
          ) : concentracionDeudor.length > 0 ? (
            <div className="space-y-2">
              {concentracionDeudor.slice(0, 5).map((deudor, index) => (
                <div key={index} className="p-2 bg-gray-50 rounded border">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <p className="font-medium text-sm">{deudor['MAX(OTO.NOM_DEUDOR)']}</p>
                      <p className="text-xs text-gray-600">RUT: {deudor.RUT_DEUDOR}</p>
                    </div>
                    <div className="text-right ml-2">
                      <p className="text-xs font-medium">MM$ {deudor.DEUDA_NETA?.toFixed(1)}</p>
                      <p className="text-xs text-gray-600">{(deudor.PER_DEUDA * 100)?.toFixed(1)}%</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-600">
              No hay datos de concentración
            </div>
          )}
        </div>
        
        <div className="col-span-2 seccion-datos">
          <h3 style={{color: 'var(--if-primary)', margin: '0 0 1rem 0', fontSize: '1.2rem', fontWeight: '600', borderLeft: '4px solid var(--if-accent)', paddingLeft: '1rem'}}>
            Volumen de Operaciones (MM$) (Sistema Factoring)
          </h3>
          {loadingGrafico ? (
            <div className="flex justify-center items-center h-32">
              <AiOutlineLoading3Quarters className="animate-spin h-8 w-8" style={{color: 'var(--if-primary)'}} />
            </div>
          ) : graficoVolumen.length > 0 ? (
            <div className="h-80">
              <div className="flex relative">
                <div className="w-12 h-64 flex flex-col justify-between text-xs text-gray-600 pr-2">
                  {(() => {
                    const maxValue = Math.max(...graficoVolumen.map(d => d.MON_ANT));
                    const steps = 5;
                    return Array.from({length: steps + 1}, (_, i) => {
                      const value = (maxValue / steps) * (steps - i);
                      return (
                        <div key={i} className="text-right">
                          {value.toFixed(0)}
                        </div>
                      );
                    });
                  })()}
                </div>
                <div className="flex-1 relative">
                  <div className="absolute inset-0">
                    {Array.from({length: 6}, (_, i) => (
                      <div 
                        key={i} 
                        className="absolute w-full border-t border-gray-200" 
                        style={{top: `${(i / 5) * 100}%`}}
                      ></div>
                    ))}
                  </div>
                  <div className="flex items-end justify-between h-64 px-4 border-b border-l border-gray-300 relative">
                    {graficoVolumen.map((item, index) => {
                      const maxValue = Math.max(...graficoVolumen.map(d => d.MON_ANT));
                      const heightPx = item.MON_ANT > 0 ? Math.max((item.MON_ANT / maxValue) * 240, 8) : 0;
                      return (
                        <div key={index} className="flex flex-col items-center" style={{width: `${100/graficoVolumen.length}%`}}>
                          {item.MON_ANT > 0 && (
                            <div 
                              className="bg-blue-500 hover:bg-blue-600 transition-colors duration-200 w-10 mx-1 rounded-t" 
                              style={{height: `${heightPx}px`}}
                              title={`${item.periodo}: MM$ ${item.MON_ANT}`}
                            ></div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
              <div className="flex mt-2">
                <div className="w-12"></div>
                <div className="flex-1 flex justify-between px-4">
                  {graficoVolumen.map((item, index) => (
                    <div key={index} className="text-xs text-center" style={{width: `${100/graficoVolumen.length}%`}}>
                      <div className="font-medium">{item.periodo}</div>
                      <div className="text-gray-600">MM$ {item.MON_ANT}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-600">
              No hay datos de volumen
            </div>
          )}
        </div>
      </div>
      
      <div className="grid grid-cols-3 gap-6 mt-6">
        <div className="seccion-datos">
          <h3 style={{color: 'var(--if-primary)', margin: '0 0 1rem 0', fontSize: '1.2rem', fontWeight: '600', borderLeft: '4px solid var(--if-accent)', paddingLeft: '1rem'}}>
            Antecedentes Societarios (Informax)
          </h3>
          {loadingAntecedentes ? (
            <div className="flex justify-center items-center h-32">
              <AiOutlineLoading3Quarters className="animate-spin h-8 w-8" style={{color: 'var(--if-primary)'}} />
            </div>
          ) : antecedentesSocietarios.length > 0 ? (
            <div className="space-y-2">
              {antecedentesSocietarios.map((persona, index) => (
                <div key={index} className="p-2 bg-gray-50 rounded border">
                  <p className="font-medium text-sm">{persona.nombre}</p>
                  <p className="text-xs text-gray-600">RUT: {persona.rut}</p>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-600">
              No hay antecedentes societarios
            </div>
          )}
        </div>
        
        <div className="col-span-2 seccion-datos">
          <h3 style={{color: 'var(--if-primary)', margin: '0 0 1rem 0', fontSize: '1.2rem', fontWeight: '600', borderLeft: '4px solid var(--if-accent)', paddingLeft: '1rem'}}>
            Top 10 Clientes de los últimos 6 meses (Informax)
          </h3>
          {loadingMaximos ? (
            <div className="flex justify-center items-center h-32">
              <AiOutlineLoading3Quarters className="animate-spin h-8 w-8" style={{color: 'var(--if-primary)'}} />
            </div>
          ) : maximosDeudores.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="text-left p-2 border" style={{ minWidth: '75px', ...thSortable }} onClick={() => handleSortMaximos('Rutcliente')}>
                      RUT {getSortIcon('Rutcliente', sortMaximos)}
                    </th>
                    <th className="text-left p-2 border" style={thSortable} onClick={() => handleSortMaximos('RazonSocial')}>
                      Razón Social {getSortIcon('RazonSocial', sortMaximos)}
                    </th>
                    <th className="text-left p-2 border" style={thSortable} onClick={() => handleSortMaximos('CLASIFICACION')}>
                      Clasificación Tamaño {getSortIcon('CLASIFICACION', sortMaximos)}
                    </th>
                    <th className="text-right p-2 border" style={thSortable} onClick={() => handleSortMaximos('Total')}>
                      Total Ventas (MM$) {getSortIcon('Total', sortMaximos)}
                    </th>
                    <th className="text-right p-2 border" style={thSortable} onClick={() => handleSortMaximos('participacion')}>
                      Participación (%) {getSortIcon('participacion', sortMaximos)}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedMaximosDeudores.map((deudor, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="p-2 border text-xs">{deudor.Rutcliente}</td>
                      <td className="p-2 border text-xs">{deudor.RazonSocial}</td>
                      <td className="p-2 border text-xs">{deudor.CLASIFICACION}</td>
                      <td className="p-2 border text-xs text-right">{deudor.Total?.toFixed(2)}</td>
                      <td className="p-2 border text-xs text-right">{(deudor.participacion * 100)?.toFixed(2)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-600">
              No hay datos de máximos deudores
            </div>
          )}
        </div>
      </div>
      
      <div className="grid grid-cols-3 gap-6 mt-6">
        <div className="seccion-datos">
          <h3 style={{color: 'var(--if-primary)', margin: '0 0 1rem 0', fontSize: '1.2rem', fontWeight: '600', borderLeft: '4px solid var(--if-accent)', paddingLeft: '1rem'}}>
            Último Dicom
          </h3>
          <div className="flex items-center">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <MdShowChart className="w-6 h-6 text-blue-600" />
            </div>
            <div className="ml-4 flex flex-col justify-center">

              <p className="text-4xl font-semibold text-gray-800">
                {loadingUltimoDicom ? (
                  <AiOutlineLoading3Quarters className="animate-spin h-6 w-6 text-blue-400" />
                ) : ultimoDicom ? Math.round(ultimoDicom.valor) : 'N/A'}
              </p>
              <p className="text-s mt-1 text-gray-600">
                {loadingUltimoDicom ? '' : ultimoDicom ? `Fecha de último informe: ${formatDateDDMMYYYY(ultimoDicom.fecha)}` : 'No hay datos disponibles'}
              </p>
            </div>
          </div>
        </div>
        
        <div className="col-span-2 seccion-datos">
          <h3 style={{color: 'var(--if-primary)', margin: '0 0 0 0', fontSize: '1.2rem', fontWeight: '600', borderLeft: '4px solid var(--if-accent)', paddingLeft: '1rem'}}>
            Evolución DICOM (Informax)
          </h3>
          {loadingDicom ? (
            <div className="flex justify-center items-center h-32">
              <AiOutlineLoading3Quarters className="animate-spin h-8 w-8" style={{color: 'var(--if-primary)'}} />
            </div>
          ) : graficoDicom.length > 0 ? (
            <div>
              <div className="relative">
<svg className="w-full h-50 border-b border-l border-gray-300" viewBox="0 0 600 140">
                  {(() => {
                    const sortedData = [...graficoDicom].reverse();
                    const maxValue = Math.max(...sortedData.map(d => d.Valor));
                    const minValue = Math.min(...sortedData.map(d => d.Valor));
                    const range = maxValue - minValue || 1;
                    const chartWidth = 500;
                    const chartHeight = 100;
                    const startX = 20;
                    const startY = 20;
                    
                    return (
                      <>
                        {/* Horizontal grid lines */}
                        {Array.from({length: 6}, (_, i) => (
                          <line
                            key={`h-grid-${i}`}
                            x1={startX}
                            y1={startY + (i * chartHeight / 5)}
                            x2={startX + chartWidth}
                            y2={startY + (i * chartHeight / 5)}
                            stroke="#e5e7eb"
                            strokeWidth="1"
                          />
                        ))}
                        {/* Vertical grid lines */}
                        {sortedData.map((_, index) => {
                          const x = startX + (index * chartWidth / (sortedData.length - 1));
                          return (
                            <line
                              key={`v-grid-${index}`}
                              x1={x}
                              y1={startY}
                              x2={x}
                              y2={startY + chartHeight}
                              stroke="#e5e7eb"
                              strokeWidth="1"
                            />
                          );
                        })}
                        {/* Connecting line */}
                        <polyline
                          fill="none"
                          stroke="#3b82f6"
                          strokeWidth="3"
                          points={sortedData.map((item, index) => {
                            const x = startX + (index * chartWidth / (sortedData.length - 1));
                            const y = startY + chartHeight - ((item.Valor - minValue) / range) * chartHeight;
                            return `${x},${y}`;
                          }).join(' ')}
                        />
                        {/* Data points */}
                        {sortedData.map((item, index) => {
                          const x = startX + (index * chartWidth / (sortedData.length - 1));
                          const y = startY + chartHeight - ((item.Valor - minValue) / range) * chartHeight;
                          return (
                            <g key={index}>
                              <circle
                                cx={x}
                                cy={y}
                                r="4"
                                fill="#3b82f6"
                                stroke="white"
                                strokeWidth="2"
                              />
                              <text
                                x={x}
                                y={y - 8}
                                textAnchor="middle"
                                className="text-xs fill-gray-700 font-medium"
                              >
                                {Math.round(item.Valor)}
                              </text>
                            </g>
                          );
                        })}
                      </>
                    );
                  })()}
                </svg>
              </div>
<div className="relative" style={{height: '40px', marginTop: '0px'}}>
<svg className="w-full h-50 " viewBox="0 0 600 120">
                  {(() => {
                    const sortedData = [...graficoDicom].reverse();
                    const chartWidth = 500;
                    const startX = 20;
                    return sortedData.map((item, index) => {
                      const x = startX + (index * chartWidth / (sortedData.length - 1));
                      return (
                        <text
                          key={index}
                          x={x}
                          y={30}
                          textAnchor="middle"
                          className="text-xs fill-gray-600 font-small"
                          transform={`rotate(-45 ${x} 15)`}
                        >
                          {item.mes_anio}
                        </text>
                      );
                    });
                  })()}
                </svg>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-600">
              No hay datos DICOM
            </div>
          )}
        </div>
      </div>
      
      <div className="mt-6">
        <div className="seccion-datos">
          <h3 style={{color: 'var(--if-primary)', margin: '0 0 1rem 0', fontSize: '1.2rem', fontWeight: '600', borderLeft: '4px solid var(--if-accent)', paddingLeft: '1rem'}}>
            Anotaciones Comerciales históricas (Informax)
          </h3>
          {loadingAnotaciones ? (
            <div className="flex justify-center items-center h-32">
              <AiOutlineLoading3Quarters className="animate-spin h-8 w-8" style={{color: 'var(--if-primary)'}} />
            </div>
          ) : anotacionesComerciales.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="text-left p-2 border" style={{ minWidth: '90px', ...thSortable }} onClick={() => handleSortAnotaciones('fechaVencimiento')}>
                      Fecha Venc. {getSortIcon('fechaVencimiento', sortAnotaciones)}
                    </th>
                    <th className="text-left p-2 border" style={{ minWidth: '90px', ...thSortable }} onClick={() => handleSortAnotaciones('fechaPublicacion')}>
                      Fecha Pub. {getSortIcon('fechaPublicacion', sortAnotaciones)}
                    </th>
                    <th className="text-left p-2 border" style={thSortable} onClick={() => handleSortAnotaciones('tipoDocumento')}>
                      Tipo {getSortIcon('tipoDocumento', sortAnotaciones)}
                    </th>
                    <th className="text-right p-2 border" style={thSortable} onClick={() => handleSortAnotaciones('monto')}>
                      Monto {getSortIcon('monto', sortAnotaciones)}
                    </th>
                    <th className="text-left p-2 border" style={thSortable} onClick={() => handleSortAnotaciones('librador')}>
                      Librador {getSortIcon('librador', sortAnotaciones)}
                    </th>
                    <th className="text-left p-2 border" style={thSortable} onClick={() => handleSortAnotaciones('motivo')}>
                      Motivo {getSortIcon('motivo', sortAnotaciones)}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedAnotaciones.slice((currentPage - 1) * 10, currentPage * 10).map((anotacion, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="p-2 border text-xs whitespace-nowrap">{formatDateDDMMYYYY(anotacion.fechaVencimiento)}</td>
                      <td className="p-2 border text-xs whitespace-nowrap">{formatDateDDMMYYYY(anotacion.fechaPublicacion)}</td>
                      <td className="p-2 border text-xs">{anotacion.tipoDocumento}</td>
                      <td className="p-2 border text-xs text-right">{anotacion.moneda} {Math.round(anotacion.monto).toLocaleString()}</td>
                      <td className="p-2 border text-xs">{anotacion.librador}</td>
                      <td className="p-2 border text-xs">{anotacion.motivo}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="flex justify-between items-center mt-4">
                <span className="text-sm text-gray-600">
                  Mostrando {Math.min((currentPage - 1) * 10 + 1, sortedAnotaciones.length)} - {Math.min(currentPage * 10, sortedAnotaciones.length)} de {sortedAnotaciones.length} registros
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1 text-sm border rounded disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Anterior
                  </button>
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.ceil(sortedAnotaciones.length / 10)))}
                    disabled={currentPage >= Math.ceil(sortedAnotaciones.length / 10)}
                    className="px-3 py-1 text-sm border rounded disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Siguiente
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-600">
              No hay anotaciones comerciales
            </div>
          )}
        </div>
      </div>
      
      <div className="grid grid-cols-3 gap-6 mt-6">
        <div className="seccion-datos">
          <h3 style={{color: 'var(--if-primary)', margin: '0 0 1rem 0', fontSize: '1.2rem', fontWeight: '600', borderLeft: '4px solid var(--if-accent)', paddingLeft: '1rem'}}>
            Distribución de Cesiones últimos 3 meses (Informax)
          </h3>
          {loadingFactoring ? (
            <div className="flex justify-center items-center h-32">
              <AiOutlineLoading3Quarters className="animate-spin h-8 w-8" style={{color: 'var(--if-primary)'}} />
            </div>
          ) : graficoFactoring.length > 0 ? (
            <div className="flex justify-center">
              <svg width="350" height="350" viewBox="0 0 200 200">
                {(() => {
                  const centerX = 100;
                  const centerY = 100;
                  const radius = 80;
                  const colorMap = {
                    'Interfactor': '#3b82f6',
                    'Banco': '#ef4444', 
                    'Otro Factoring': '#10b981'
                  };
                  
                  const getColor = (tipo) => colorMap[tipo] || '#6b7280';
                  
                  // Si solo hay un elemento con 100%, mostrar círculo completo
                  if (graficoFactoring.length === 1 && graficoFactoring[0].PER_CESION >= 0.99) {
                    const item = graficoFactoring[0];
                    return (
                      <g>
                        <circle
                          cx={centerX}
                          cy={centerY}
                          r={radius}
                          fill={getColor(item.TIPO_FACTORING)}
                          stroke="white"
                          strokeWidth="2"
                        />
                        <text
                          x={centerX}
                          y={centerY}
                          textAnchor="middle"
                          dominantBaseline="middle"
                          className="text-sm fill-black"
                        >
                          MM$ {item.CESION?.toFixed(1)}
                        </text>
                      </g>
                    );
                  }
                  
                  let currentAngle = -90;
                  
                  return graficoFactoring.map((item, index) => {
                    const percentage = item.PER_CESION || 0;
                    const angle = percentage * 360;
                    
                    if (angle === 0) return null;
                    
                    const startAngle = currentAngle;
                    const endAngle = currentAngle + angle;
                    
                    const startAngleRad = (startAngle * Math.PI) / 180;
                    const endAngleRad = (endAngle * Math.PI) / 180;
                    
                    const x1 = centerX + radius * Math.cos(startAngleRad);
                    const y1 = centerY + radius * Math.sin(startAngleRad);
                    const x2 = centerX + radius * Math.cos(endAngleRad);
                    const y2 = centerY + radius * Math.sin(endAngleRad);
                    
                    const largeArcFlag = angle > 180 ? 1 : 0;
                    
                    const pathData = [
                      `M ${centerX} ${centerY}`,
                      `L ${x1} ${y1}`,
                      `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}`,
                      'Z'
                    ].join(' ');
                    
                    currentAngle += angle;
                    
                    const midAngle = (startAngle + endAngle) / 2;
                    const textRadius = radius * 0.7;
                    const textX = centerX + textRadius * Math.cos((midAngle * Math.PI) / 180);
                    const textY = centerY + textRadius * Math.sin((midAngle * Math.PI) / 180);
                    
                    return (
                      <g key={index}>
                        <path
                          d={pathData}
                          fill={getColor(item.TIPO_FACTORING)}
                          stroke="white"
                          strokeWidth="2"
                        />
                        <text
                          x={textX}
                          y={textY}
                          textAnchor="middle"
                          dominantBaseline="middle"
                          className="text-xs fill-black"
                        >
                          MM$ {item.CESION?.toFixed(1)}
                        </text>
                      </g>
                    );
                  }).filter(Boolean);
                })()}
              </svg>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-600">
              No hay datos de factoring
            </div>
          )}
          {graficoFactoring.length > 0 && (
            <div className="mt-4 space-y-2">
              {graficoFactoring.map((item, index) => {
                const colorMap = {
                  'Interfactor': '#3b82f6',
                  'Banco': '#ef4444', 
                  'Otro Factoring': '#10b981'
                };
                const getColor = (tipo) => colorMap[tipo] || '#6b7280';
                return (
                  <div key={index} className="flex items-center text-sm">
                    <div 
                      className="w-3 h-3 rounded mr-2" 
                      style={{backgroundColor: getColor(item.TIPO_FACTORING)}}
                    ></div>
                    <span className="flex-1">{item.TIPO_FACTORING}</span>
                    <span className="font-medium">{(item.PER_CESION * 100).toFixed(1)}%</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        
        <div className="col-span-2 seccion-datos">
          <div className="flex justify-between items-center mb-4">
            <h3 style={{color: 'var(--if-primary)', margin: '0', fontSize: '1.2rem', fontWeight: '600', borderLeft: '4px solid var(--if-accent)', paddingLeft: '1rem'}}>
              Detalle de Cesiones últimos 3 meses (Informax)
            </h3>
            {detalleCesiones.length > 0 && (
              <button
                onClick={() => {
                  const data = sortedDetalleCesiones.map(cesion => ({
                    'Fecha Cesión': formatDateDDMMYYYY(cesion.FCH_CESION),
                    'Deudor': cesion[CESION_DEUDOR_KEY],
                    'Folio': cesion.FOLIO_DOC,
                    'Tipo Doc': cesion.TIPO_DOC,
                    'Monto (MM$)': cesion[CESION_MONTO_KEY]?.toFixed(2).replace('.', ','),
                    'Cesionario': cesion.RZ_CESIONARIO
                  }));
                  const ws = XLSX.utils.json_to_sheet(data);
                  const wb = XLSX.utils.book_new();
                  XLSX.utils.book_append_sheet(wb, ws, 'Detalle Cesiones');
                  XLSX.writeFile(wb, `detalle_cesiones_${new Date().toISOString().split('T')[0]}.xlsx`);
                }}
                className="btn-primary"
                style={{
                  background: 'linear-gradient(135deg, var(--if-success) 0%, #16a34a 100%)',
                  padding: '0.5rem 1rem',
                  fontSize: '0.875rem',
                  borderLeft: '3px solid var(--if-primary)'
                }}
              >
                <MdReceipt className="w-4 h-4 mr-2" style={{display: 'inline'}} />
                Exportar Excel
              </button>
            )}
          </div>
          {loadingCesiones ? (
            <div className="flex justify-center items-center h-32">
              <AiOutlineLoading3Quarters className="animate-spin h-8 w-8" style={{color: 'var(--if-primary)'}} />
            </div>
          ) : detalleCesiones.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="text-left p-2 border" style={{ minWidth: '75px', ...thSortable }} onClick={() => handleSortCesiones('FCH_CESION')}>
                      Fecha Cesión {getSortIcon('FCH_CESION', sortCesiones)}
                    </th>
                    <th className="text-left p-2 border" style={{ minWidth: '250px', ...thSortable }} onClick={() => handleSortCesiones(CESION_DEUDOR_KEY)}>
                      Deudor {getSortIcon(CESION_DEUDOR_KEY, sortCesiones)}
                    </th>
                    <th className="text-center p-2 border" style={thSortable} onClick={() => handleSortCesiones('FOLIO_DOC')}>
                      Folio {getSortIcon('FOLIO_DOC', sortCesiones)}
                    </th>
                    <th className="text-center p-2 border" style={thSortable} onClick={() => handleSortCesiones('TIPO_DOC')}>
                      Tipo Doc {getSortIcon('TIPO_DOC', sortCesiones)}
                    </th>
                    <th className="text-right p-2 border" style={thSortable} onClick={() => handleSortCesiones(CESION_MONTO_KEY)}>
                      Monto (MM$) {getSortIcon(CESION_MONTO_KEY, sortCesiones)}
                    </th>
                    <th className="text-left p-2 border" style={{ minWidth: '180px', ...thSortable }} onClick={() => handleSortCesiones('RZ_CESIONARIO')}>
                      Cesionario {getSortIcon('RZ_CESIONARIO', sortCesiones)}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedDetalleCesiones.slice((currentPageCesiones - 1) * 15, currentPageCesiones * 15).map((cesion, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="p-2 border text-xs">{formatDateDDMMYYYY(cesion.FCH_CESION)}</td>
                      <td className="p-2 border text-xs">{cesion[CESION_DEUDOR_KEY]}</td>
                      <td className="p-2 border text-xs text-center">{cesion.FOLIO_DOC}</td>
                      <td className="p-2 border text-xs text-center">{cesion.TIPO_DOC}</td>
                      <td className="p-2 border text-xs text-right">{cesion[CESION_MONTO_KEY]?.toFixed(2)}</td>
                      <td className="p-2 border text-xs">{cesion.RZ_CESIONARIO}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="flex justify-between items-center mt-4">
                <span className="text-sm text-gray-600">
                  Mostrando {Math.min((currentPageCesiones - 1) * 15 + 1, sortedDetalleCesiones.length)} - {Math.min(currentPageCesiones * 15, sortedDetalleCesiones.length)} de {sortedDetalleCesiones.length} registros
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={() => setCurrentPageCesiones(prev => Math.max(prev - 1, 1))}
                    disabled={currentPageCesiones === 1}
                    className="px-3 py-1 text-sm border rounded disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Anterior
                  </button>
                  <button
                    onClick={() => setCurrentPageCesiones(prev => Math.min(prev + 1, Math.ceil(sortedDetalleCesiones.length / 15)))}
                    disabled={currentPageCesiones >= Math.ceil(sortedDetalleCesiones.length / 15)}
                    className="px-3 py-1 text-sm border rounded disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Siguiente
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-600">
              No hay datos de cesiones
            </div>
          )}
        </div>
      </div>
      
      <div className="mt-6">
        <div className="seccion-datos">
          <div className="flex justify-between items-center mb-4">
            <h3 style={{color: 'var(--if-primary)', margin: '0', fontSize: '1.2rem', fontWeight: '600', borderLeft: '4px solid var(--if-accent)', paddingLeft: '1rem'}}>
              Detalle de Facturación últimos 3 meses (Informax)
            </h3>
            {detalleFacturas.length > 0 && (
              <button
                onClick={() => {
                  const data = sortedDetalleFacturas.map(factura => ({
                    'Fecha Documento': formatDate(factura.FechaDocto),
                    'RUT Cliente': factura.Rutcliente,
                    'Razón Social': factura.RazonSocial,
                    'Folio': factura.Folio,
                    'Tipo Doc': factura.TipoDoc,
                    'Estado Reclamo': factura.ESTADO_RECLAMO,
                    'Monto Total': factura.Montototal?.toLocaleString('es-CL'),
                    'Monto Notas Crédito': factura.MONTO_NOTAS_CREDITO?.toLocaleString('es-CL'),
                    'Estado Cesión': factura.ESTADO_CESION,
                    'Factoring': factura.NOMBRE_FACTORING
                  }));
                  const ws = XLSX.utils.json_to_sheet(data);
                  const wb = XLSX.utils.book_new();
                  XLSX.utils.book_append_sheet(wb, ws, 'Detalle Facturas');
                  XLSX.writeFile(wb, `detalle_facturas_${new Date().toISOString().split('T')[0]}.xlsx`);
                }}
                className="btn-primary"
                style={{
                  background: 'linear-gradient(135deg, var(--if-success) 0%, #16a34a 100%)',
                  padding: '0.5rem 1rem',
                  fontSize: '0.875rem',
                  borderLeft: '3px solid var(--if-primary)'
                }}
              >
                <MdReceipt className="w-4 h-4 mr-2" style={{display: 'inline'}} />
                Exportar Excel
              </button>
            )}
          </div>
          {loadingFacturas ? (
            <div className="flex justify-center items-center h-32">
              <AiOutlineLoading3Quarters className="animate-spin h-8 w-8" style={{color: 'var(--if-primary)'}} />
            </div>
          ) : detalleFacturas.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="text-left p-2 border" style={thSortable} onClick={() => handleSortFacturas('FechaDocto')}>
                      Fecha Doc {getSortIcon('FechaDocto', sortFacturas)}
                    </th>
                    <th className="text-left p-2 border" style={{ minWidth: '250px', ...thSortable }} onClick={() => handleSortFacturas('RazonSocial')}>
                      Razón Social {getSortIcon('RazonSocial', sortFacturas)}
                    </th>
                    <th className="text-center p-2 border" style={thSortable} onClick={() => handleSortFacturas('Folio')}>
                      Folio {getSortIcon('Folio', sortFacturas)}
                    </th>
                    <th className="text-center p-2 border" style={thSortable} onClick={() => handleSortFacturas('TipoDoc')}>
                      Tipo {getSortIcon('TipoDoc', sortFacturas)}
                    </th>
                    <th className="text-right p-2 border" style={thSortable} onClick={() => handleSortFacturas('Montototal')}>
                      Monto Total {getSortIcon('Montototal', sortFacturas)}
                    </th>
                    <th className="text-right p-2 border" style={thSortable} onClick={() => handleSortFacturas('MONTO_NOTAS_CREDITO')}>
                      Notas Crédito {getSortIcon('MONTO_NOTAS_CREDITO', sortFacturas)}
                    </th>
                    <th className="text-center p-2 border" style={thSortable} onClick={() => handleSortFacturas('ESTADO_RECLAMO')}>
                      Estado Reclamo {getSortIcon('ESTADO_RECLAMO', sortFacturas)}
                    </th>
                    <th className="text-center p-2 border" style={thSortable} onClick={() => handleSortFacturas('ESTADO_CESION')}>
                      Estado Cesión {getSortIcon('ESTADO_CESION', sortFacturas)}
                    </th>
                    <th className="text-left p-2 border" style={{ minWidth: '150px', ...thSortable }} onClick={() => handleSortFacturas('NOMBRE_FACTORING')}>
                      Factoring {getSortIcon('NOMBRE_FACTORING', sortFacturas)}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedDetalleFacturas.slice((currentPageFacturas - 1) * 15, currentPageFacturas * 15).map((factura, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="p-2 border text-xs">{formatDate(factura.FechaDocto)}</td>
                      <td className="p-2 border text-xs">{factura.RazonSocial}</td>
                      <td className="p-2 border text-xs text-center">{factura.Folio}</td>
                      <td className="p-2 border text-xs text-center">{factura.TipoDoc}</td>
                      <td className="p-2 border text-xs text-right">$ {factura.Montototal?.toLocaleString('es-CL')}</td>
                      <td className="p-2 border text-xs text-right">$ {factura.MONTO_NOTAS_CREDITO?.toLocaleString('es-CL')}</td>
                      <td className="p-2 border text-xs text-center">{factura.ESTADO_RECLAMO || '-'}</td>
                      <td className="p-2 border text-xs text-center">{factura.ESTADO_CESION}</td>
                      <td className="p-2 border text-xs">{factura.NOMBRE_FACTORING}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="flex justify-between items-center mt-4">
                <span className="text-sm text-gray-600">
                  Mostrando {Math.min((currentPageFacturas - 1) * 15 + 1, sortedDetalleFacturas.length)} - {Math.min(currentPageFacturas * 15, sortedDetalleFacturas.length)} de {sortedDetalleFacturas.length} registros
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={() => setCurrentPageFacturas(prev => Math.max(prev - 1, 1))}
                    disabled={currentPageFacturas === 1}
                    className="px-3 py-1 text-sm border rounded disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Anterior
                  </button>
                  <button
                    onClick={() => setCurrentPageFacturas(prev => Math.min(prev + 1, Math.ceil(sortedDetalleFacturas.length / 15)))}
                    disabled={currentPageFacturas >= Math.ceil(sortedDetalleFacturas.length / 15)}
                    className="px-3 py-1 text-sm border rounded disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Siguiente
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-600">
              No hay datos de facturas
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DetalleCliente;